import {
  Button,
  Card,
  Col,
  Container,
  FormGroup,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import NOPHOTO from "../../src/assets/img/icons/common/noPhoto.png";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import moment from "moment";
import { Image, notification, Spin as AntSpinner } from "antd";
import { CallGetUserByUserId, CallUpdateFingerprint } from "services/apiAction";

const fingerprintPatterns = [
  "Arch",
  "Arch Radial Loop",
  "Arch Ulnar Loop",
  "Radial Loop",
  "Spiral",
  "Ulnar Loop",
  "Centric Whorl",
  "Elongated Whorl",
  "Double Loop",
  "Accidental",
];

const patternCodes = {
  Arch: "X1",
  "Arch Radial Loop": "R1",
  "Radial Loop": "R",
  "Arch Ulnar Loop": "L1",
  "Ulnar Loop": "L",
  "Centric Whorl": "W4",
  Spiral: "W2",
  "Elongated Whorl": "W3",
  "Double Loop": "W5",
  Accidental: "W",
};

const Profile = () => {
  const AuthToken = localStorage.getItem("token");
  const [api, contextHolder] = notification.useNotification();
  const [data, setData] = useState(null);
  const { control, setValue, getValues } = useForm();
  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await CallGetUserByUserId(id);
      if (response?.data?.message === "Success") {
        setData(response.data.UserData);

        // Set initial fingerprint data to form
        const fingers = [
          "fingerLeftThumb",
          "fingerLeftIndex",
          "fingerLeftMiddle",
          "fingerLeftRing",
          "fingerLeftLittle",
          "fingerRightThumb",
          "fingerRightIndex",
          "fingerRightMiddle",
          "fingerRightRing",
          "fingerRightLittle",
        ];
        fingers.forEach((finger) => {
          if (response.data.UserData[finger]) {
            setValue(`${finger}`, response.data.UserData[finger]);
          }
        });
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      api.error({ message: "Failed to load user data" });
    } finally {
      setLoading(false);
    }
  };

  const setPatternCode = (finger, pattern) => {
    const code = patternCodes[pattern];
    if (code) setValue(`${finger}.pattern_code`, code);
  };

  const handleUpdate = async () => {
    try {
      setUpdateLoading(true);

      const formData = new FormData();
      const values = getValues();

      Object.keys(values).forEach((key) => {
        if (values[key]) setPatternCode(key, values[key].fingerprint_pattern);
      });

      Object.keys(values).forEach((key) => {
        if (values[key]) formData.append(key, JSON.stringify(values[key]));
      });

      const response = await CallUpdateFingerprint(id, formData);
      if (response?.data?.message === "Success") {
        setData(response.data.UserData);
        api.success({ message: "Profile updated successfully", duration: 2 });
        fetchData();
      }
    } catch (error) {
      console.error("Update failed:", error);
      api.error({ message: "Failed to update profile" });
    } finally {
      // Full page loader for 3 seconds
      setTimeout(() => {
        setUpdateLoading(false);
      }, 3000);
    }
  };

  const handleReport = async () => {
    try {
      setReportLoading(true);

      const res = await fetch("https://apidaksh.dakshtest.com/api/admin/createReport", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${AuthToken}`,
        },
        body: JSON.stringify({ userDataId: data?._id }),
      });

      const result = await res.json();

      if (result) {
        const role = data?.role;
        let parent = "user";
        if (role === "vendor") parent = "vendor";
        else if (role === "school") parent = "school";

        navigate(`/admin/${parent}/index/user-profile/report/${data?._id}`);
      }
    } catch (error) {
      console.error("Report generation failed:", error);
      api.error({ message: "Failed to generate report" });
    } finally {
      setReportLoading(false);
    }
  };

  const renderFingerInput = (finger, label) => (
    <div className="personalDetail" key={finger}>
      <div className="img_container">
        <Image
          width="100%"
          src={data?.[finger]?.fingerprint_image || NOPHOTO}
        />
      </div>
      <div className="fingerDetails">
        <div className="name">
          <span>{label}</span>
        </div>
        <FormGroup>
          <Controller
            name={`${finger}.fingerprint_pattern`}
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input type="select" value={value || ""} onChange={onChange}>
                <option value="">Fingerprint pattern</option>
                {fingerprintPatterns.map((pattern) => (
                  <option key={pattern} value={pattern}>
                    {pattern}
                  </option>
                ))}
              </Input>
            )}
          />
          <Controller
            name={`${finger}.ridge_count`}
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                type="number"
                placeholder="Ridge count"
                value={value || ""}
                onChange={onChange}
              />
            )}
          />
        </FormGroup>
      </div>
    </div>
  );

  return (
    <Container className="mt-5 mb-3" fluid>
      {contextHolder}

      {(loading || updateLoading) && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "80%",
            height: "100%",
            marginLeft: "260px",
            backgroundColor: "rgba(255,255,255,0.7)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <AntSpinner size="medium" tip={updateLoading ? "Updating..." : "Loading..."} />
        </div>
      )}

      <Row>
        <Col xl="4">
          <Card className="p-3">
            <table className="personlDetails-table">
              <tbody>
                <tr>
                  <th colSpan={2}>Personal Details</th>
                </tr>
                <tr>
                  <td>Name</td>
                  <td><b>{data?.dataName?.toUpperCase()}</b></td>
                </tr>
                <tr>
                  <td>Mobile</td>
                  <td><b>{data?.mobile}</b></td>
                </tr>
                {data?.role !== "applicant" && (
                  <tr>
                    <td>Class</td>
                    <td><b>{data?.class}</b></td>
                  </tr>
                )}
                <tr>
                  <td>Father Name</td>
                  <td><b>{data?.father_name}</b></td>
                </tr>
                <tr>
                  <td>DOB</td>
                  <td><b>{moment(data?.dob).format("ll")}</b></td>
                </tr>
                <tr>
                  <td>Status</td>
                  <td>{data?.status ? "Active" : "Inactive"}</td>
                </tr>
                <tr>
                  <td>Created Date</td>
                  <td>{data?.created_at ? moment(data.created_at).format("LL") : "--"}</td>
                </tr>
                <tr>
                  <td>Updated Date</td>
                  <td>{data?.updated_at ? moment(data.updated_at).format("LL") : "--"}</td>
                </tr>
              </tbody>
            </table>
          </Card>
        </Col>

        <Col xl="8">
          <Card className="profileDetails">
            <Row>
              <Col xl="6">
                {["fingerLeftThumb", "fingerLeftIndex", "fingerLeftMiddle", "fingerLeftRing", "fingerLeftLittle"].map(
                  (finger, i) => renderFingerInput(finger, `Left ${["Thumb", "Index", "Middle", "Ring", "Little"][i]}`)
                )}
              </Col>
              <Col xl="6">
                {["fingerRightThumb", "fingerRightIndex", "fingerRightMiddle", "fingerRightRing", "fingerRightLittle"].map(
                  (finger, i) => renderFingerInput(finger, `Right ${["Thumb", "Index", "Middle", "Ring", "Little"][i]}`)
                )}
              </Col>
            </Row>
            <div className="btn-container d-flex justify-content-between mb-3">
              <Button
                color="primary"
                onClick={handleReport}
                disabled={reportLoading || loading || updateLoading}
              >
                {reportLoading ? <Spinner size="sm" /> : "Report"}
              </Button>
              <Button
                color="primary"
                onClick={handleUpdate}
                disabled={updateLoading || loading}
              >
                {updateLoading ? <AntSpinner size="small" /> : "Update"}
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Profile;